import { buildChatWebSocketUrl } from "./chatConfig";

export const CONNECTION_STATES = {
    CONNECTING: "CONNECTING",
    CONNECTED: "CONNECTED",
    DISCONNECTED: "DISCONNECTED",
    ERROR: "ERROR",
};

/**
 * Generate a unique client request ID
 */
export const generateRequestId = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    // Fallback if crypto.randomUUID is not available
    return "req-" + Date.now() + "-" + Math.random().toString(36).substring(2, 9);
};

export class ChatWebSocketService {
    constructor(cin, token) {
        this.cin = cin;
        this.token = token;
        this.socket = null;
        this.connectionState = CONNECTION_STATES.DISCONNECTED;
        this.statusListeners = new Set();
        this.messageListeners = new Set();

        // Reconnection configuration
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 6;
        this.baseReconnectDelayMs = 1000;
        this.maxReconnectDelayMs = 30000;
        this.reconnectTimer = null;
        this.isManualClose = false;
    }

    /**
     * Subscribe to connection status changes
     * @param {Function} callback (state: CONNECTION_STATES, errorDetail?: string)
     * @returns {Function} Unsubscribe function
     */
    onStatusChange(callback) {
        this.statusListeners.add(callback);
        // Immediately notify current state
        callback(this.connectionState);
        return () => this.statusListeners.delete(callback);
    }

    /**
     * Subscribe to incoming messages
     * @param {Function} callback (message: Object)
     * @returns {Function} Unsubscribe function
     */
    onMessage(callback) {
        this.messageListeners.add(callback);
        return () => this.messageListeners.delete(callback);
    }

    notifyStatus(state, errorDetail = null) {
        this.connectionState = state;
        this.statusListeners.forEach((callback) => {
            try {
                callback(state, errorDetail);
            } catch (err) {
                console.error("Error in status listener:", err);
            }
        });
    }

    notifyMessage(data) {
        this.messageListeners.forEach((callback) => {
            try {
                callback(data);
            } catch (err) {
                console.error("Error in message listener:", err);
            }
        });
    }

    /**
     * Connect to the WebSocket server
     */
    connect(forcedToken = null, forcedCin = null) {
        if (forcedToken !== undefined && forcedToken !== null) {
            this.token = forcedToken;
        }
        if (forcedCin) {
            this.cin = forcedCin;
        }

        // Prevent multiple simultaneous connections
        if (
            this.socket &&
            (this.socket.readyState === WebSocket.OPEN ||
                this.socket.readyState === WebSocket.CONNECTING)
        ) {
            return;
        }

        this.isManualClose = false;
        this.clearReconnectTimer();
        this.notifyStatus(CONNECTION_STATES.CONNECTING);

        const url = buildChatWebSocketUrl(this.cin, this.token);

        try {
            this.socket = new WebSocket(url);

            this.socket.onopen = () => {
                this.reconnectAttempts = 0;
                this.notifyStatus(CONNECTION_STATES.CONNECTED);
            };

            this.socket.onmessage = (event) => {
                try {
                    const parsed = JSON.parse(event.data);
                    this.notifyMessage(parsed);
                } catch (e) {
                    console.error("Failed to parse WebSocket message:", event.data, e);
                    this.notifyMessage({
                        type: "error",
                        detail: "Invalid message format received from server.",
                    });
                }
            };

            this.socket.onerror = (error) => {
                console.error("WebSocket connection error:", error);
                this.notifyStatus(
                    CONNECTION_STATES.ERROR,
                    "WebSocket encountered a connection error. The server may require header authentication or is unreachable."
                );
            };

            this.socket.onclose = (event) => {
                this.socket = null;
                const wasClean = event.wasClean;
                const code = event.code;
                const reason = event.reason;

                this.notifyStatus(
                    CONNECTION_STATES.DISCONNECTED,
                    code ? `Disconnected (Code: ${code}${reason ? ` - ${reason}` : ""})` : "Disconnected"
                );

                // Only reconnect if disconnection was involuntary
                if (!this.isManualClose) {
                    this.scheduleReconnect();
                }
            };
        } catch (err) {
            console.error("Failed to instantiate WebSocket:", err);
            this.notifyStatus(
                CONNECTION_STATES.ERROR,
                err.message || "Failed to initialize WebSocket"
            );
            if (!this.isManualClose) {
                this.scheduleReconnect();
            }
        }
    }

    /**
     * Schedule reconnection with exponential backoff
     */
    scheduleReconnect() {
        if (this.reconnectTimer || this.isManualClose) return;

        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.notifyStatus(
                CONNECTION_STATES.DISCONNECTED,
                "Maximum reconnection attempts reached. Click Reconnect to retry."
            );
            return;
        }

        const delay = Math.min(
            this.baseReconnectDelayMs * Math.pow(2, this.reconnectAttempts),
            this.maxReconnectDelayMs
        );

        this.reconnectAttempts += 1;

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.connect();
        }, delay);
    }

    clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    /**
     * Send a question message to the WebSocket
     * @param {string} question - Question text (1 - 2000 chars)
     * @param {string} [customId] - Optional custom ID
     * @returns {string} The request ID
     */
    sendMessage(question, customId = null) {
        if (!question || typeof question !== "string" || !question.trim()) {
            throw new Error("Question cannot be blank.");
        }

        const cleanQuestion = question.trim();
        if (cleanQuestion.length > 2000) {
            throw new Error("Question exceeds maximum length of 2000 characters.");
        }

        if (
            !this.socket ||
            this.socket.readyState !== WebSocket.OPEN
        ) {
            throw new Error("WebSocket is not currently connected.");
        }

        const id = customId || generateRequestId();
        const payload = {
            id,
            question: cleanQuestion,
        };

        this.socket.send(JSON.stringify(payload));
        return id;
    }

    /**
     * Manually trigger reconnection
     */
    reconnect() {
        this.disconnect();
        this.reconnectAttempts = 0;
        this.connect();
    }

    /**
     * Disconnect and clean up
     */
    disconnect() {
        this.isManualClose = true;
        this.clearReconnectTimer();

        if (this.socket) {
            try {
                this.socket.close();
            } catch (e) {
                // Ignore close error
            }
            this.socket = null;
        }

        this.notifyStatus(CONNECTION_STATES.DISCONNECTED);
    }

    /**
     * Completely clean up instance
     */
    destroy() {
        this.disconnect();
        this.statusListeners.clear();
        this.messageListeners.clear();
    }
}
